// src/components/LocationInput.js
import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography
} from '@mui/material';
import { FaLocationArrow } from 'react-icons/fa';
import { fetchCitySuggestionsAPI, fetchLocationByCoords } from '../api';

const CSS = `
  @keyframes locationPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  .gong-location-btn {
    width: 40px;
    height: 56px;
    flex-shrink: 0;
    background: #7eb8d4;
    border: none;
    border-left: 1.5px solid rgba(0,0,0,0.1);
    border-right: 1.5px solid rgba(0,0,0,0.1);
    color: rgba(255,255,255,0.8);
    cursor: pointer;
    transition: color 0.2s, background 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .gong-location-btn:hover {
    color: #fff;
    background: #6aaac4;
  }
  .gong-location-btn:hover::after {
    content: 'Use my location';
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: #2a2a20;
    border: 1px solid rgba(240,236,224,0.15);
    color: rgba(240,236,224,0.8);
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.1em;
    padding: 4px 8px;
    border-radius: 2px;
    white-space: nowrap;
    pointer-events: none;
    z-index: 10;
  }
  .gong-location-btn.loading {
    animation: locationPulse 1s ease-in-out infinite;
  }
`;

export default function LocationInput({ onLocationSubmit, initialLocation = "" }) {
  const [inputValue, setInputValue] = useState(initialLocation);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const debounceTimeout = useRef(null);

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }
    setLocationLoading(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;
        setInputValue('');
        setLocationLoading(false);
        onLocationSubmit({ lat, lon });
      },
      (err) => {
        setLocationLoading(false);
        if (err.code === err.TIMEOUT) {
          setLocationError('Location request timed out');
        } else {
          setLocationError('Location unavailable');
        }
      },
      { timeout: 5000 }
    );
  };

  useEffect(() => {
    if (!navigator.permissions) return;
    navigator.permissions.query({ name: 'geolocation' }).then((result) => {
      if (result.state === 'granted') {
        handleUseLocation();
      }
    });
  }, []);

  const handleFetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setIsLoadingSuggestions(true);
    setSuggestionsError('');
    try {
      const data = await fetchCitySuggestionsAPI(query);
      setSuggestions(data);
      setShowSuggestions(data.length > 0);
    } catch (err) {
      setSuggestionsError('Could not load suggestions.');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSuggestionsError('');

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    if (value.trim() === "") {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
      return;
    }
    setIsLoadingSuggestions(true);
    debounceTimeout.current = setTimeout(() => {
      handleFetchSuggestions(value);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    const displayName = `${suggestion.name}${suggestion.state ? `, ${suggestion.state}` : ''}, ${suggestion.country}`;
    setInputValue(displayName);
    setSuggestions([]);
    setShowSuggestions(false);
    onLocationSubmit(displayName);
  };

  const handleSubmit = () => {
    if (inputValue.trim()) {
      onLocationSubmit(inputValue.trim());
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const getBottomMargin = () => {
    if (isLoadingSuggestions || suggestionsError || locationError || (showSuggestions && suggestions.length > 0)) {
      return 8;
    }
    return 3;
  };

  return (
    <Box sx={{
      display: "flex",
      gap: 0,
      mb: getBottomMargin(),
      width: '100%',
      maxWidth: 400,
      position: 'relative',
      justifyContent: 'center',
      mx: 'auto'
    }}>
      <style>{CSS}</style>
      <TextField
        fullWidth
        placeholder="Enter location"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (inputValue.trim().length > 0 && suggestions.length > 0 && !isLoadingSuggestions) {
            setShowSuggestions(true);
          }
        }}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#F8F8F8',
            '& fieldset': { borderColor: 'white' },
            '&:hover fieldset': { borderColor: 'white' },
            '&.Mui-focused fieldset': { borderColor: 'white' },
          },
          '& .MuiInputBase-input::placeholder': { color: 'gray', opacity: 1 },
          '& .MuiOutlinedInput-input': { color: 'black' },
          borderRadius: '4px 0 0 4px',
          overflow: 'hidden'
        }}
      />

      <button
        className={`gong-location-btn${locationLoading ? ' loading' : ''}`}
        onClick={handleUseLocation}
        type="button"
        aria-label="Use my location"
      >
        <FaLocationArrow size={14} />
      </button>

      <Button
        variant="contained"
        onClick={handleSubmit}
        sx={{
          backgroundColor: '#FF5722',
          color: '#F8F8F8',
          fontWeight: 'bold',
          height: '56px',
          borderRadius: '0 4px 4px 0',
          '&:hover': { backgroundColor: '#E64A19' },
        }}
      >
        FIRE
      </Button>

      {/* Suggestions / error area */}
      <Box sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 3,
        mt: 0.5,
      }}>
        {locationError && !locationLoading && (
          <Box sx={{ p: 1, backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', boxShadow: 3, textAlign: 'center' }}>
            <Typography variant="caption">{locationError}</Typography>
          </Box>
        )}

        {isLoadingSuggestions && (
          <Box sx={{ p: 1, backgroundColor: '#F8F8F8', borderRadius: '4px', boxShadow: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {suggestionsError && !isLoadingSuggestions && (
          <Box sx={{ p: 1, backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', boxShadow: 3, textAlign: 'center' }}>
            <Typography variant="caption">{suggestionsError}</Typography>
          </Box>
        )}

        {!isLoadingSuggestions && !suggestionsError && showSuggestions && suggestions.length > 0 && (
          <Box
            sx={{
              maxHeight: 200,
              overflowY: 'auto',
              backgroundColor: '#F8F8F8',
              borderRadius: '4px',
              boxShadow: 3,
              color: 'black'
            }}
          >
            <List disablePadding>
              {suggestions.map((suggestion, index) => (
                <ListItemButton
                  key={suggestion.lat && suggestion.lon ? `${suggestion.lat}-${suggestion.lon}-${index}` : index}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{ py: 1, px: 2 }}
                >
                  <ListItemText
                    primary={`${suggestion.name}${suggestion.state ? `, ${suggestion.state}` : ''}`}
                    secondary={suggestion.country}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Box>
  );
}
